import type { RouterLinkProps } from 'vue-router';

import type { ColumnNode } from 'primevue';

import type { AvatarProps } from '@/dsComponents/Avatar';
import type { ButtonAltProps, ButtonProps } from '@/dsComponents/buttons/types';
import type {
	DropdownDirections,
	DropdownGeneratorActionType,
	DropdownItemProps,
	DropdownTitleProps,
} from '@/dsComponents/Dropdown';

export enum TableFontColor {
	blue = 'blue',
	purple = 'purple',
	red = 'red',
	orange = 'orange',
	yellow = 'yellow',
	green = 'green',
	grey = 'grey',
	black = 'black',
}
export interface TableColumnProps {
	field: string;
	header: string;
	sortable?: boolean;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	props?: any;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	component?: any;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	sortField?: string | ((colData: any) => string);
	width?: string;
	spacing?: TableSpacing;
	headerSpacing?: TableSpacing;
	/**  To render a router link */
	link?: boolean;
	actions?: boolean;
	minWidth?: string;
	maxWidth?: string;
	style?: object;
	bordered?: boolean;
	frozen?: boolean;
	alignFrozen?: string;
	headerDropdown?: TableColumnHeaderDropdown;
}

export interface TableColumnHeaderDropdown {
	mode?: 'click' | 'hover';
	width?: string;
	direction?: DropdownDirections;
	nostyle?: boolean;
	content?: string;
	// biome-ignore lint/suspicious/noExplicitAny: matches TableColumnProps.component
	component?: any;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	componentProps?: Record<string, any>;
}

export enum TableSpacing {
	XS = 'xs',
	SM = 'sm',
	MD = 'md',
}

export interface TableRowLink extends RouterLinkProps {
	label: string;
}

export interface TableTriggerEmit {
	rowIndex: number;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: any;
	itemIndex: number;
	dropdownItems: DropdownItemPayload;
}

export interface DropdownItemPayload extends DropdownItemProps {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	onClick?: (item: any) => void;
}

export interface DropdownCollection {
	type: DropdownGeneratorActionType;
	key?: string;
	item?: DropdownItemPayload;
	title?: DropdownTitleProps;
}

export interface TableAction {
	label?: string;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	onClick?: (item: any, index?: number) => void;
	button: ButtonProps;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	dropdownItems?: (item: any) => DropdownCollection[];
	width?: string;
	closeOnDescendant?: boolean;
	dropdownMode?: 'click' | 'hover';
}

export interface TableActionsProps {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: any;
	idKey: string;
	actions?: TableAction[];
}

export interface TableRowTriggerEmit {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: { data: any };
	originalEvent?: Event;
	index?: number;
}

export interface TableActionTriggerEmit {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: { data: any };
	itemIndex: number;
	dropdownItems?: DropdownItemPayload;
}

export interface TableCellProps {
	cellTitle: string;
	description?: string;
	imageUrl?: string;
	fontType?: 'leading' | 'regular' | 'passive';
	fontColor?: TableFontColor;
	avatarProps?: AvatarProps;
	icon?: IconTableCell;
	size?: 'md' | 'sm';
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	onClick?: (item: any) => void;
}

export interface ISort {
	sortField: string;
	sortOrder: number;
}

export interface IconTableCell {
	iconType: string;
	iconColor?: 'green' | 'red' | 'yellow' | 'blue' | 'dark-grey' | 'grey';
}

export interface CellMultiLabelProps {
	size?: 'md' | 'sm';
	cellContent: CellMultiLabelContent[];
}

export interface CellMultiLabelContent {
	label: string;
	icon?: IconTableCell;
	priority?: 'leading' | 'regular' | 'passive';
}

export interface TableMassAction {
	button: ButtonAltProps;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	onClick?: (items: any[]) => void | boolean | Promise<void> | Promise<boolean>;
}

// Primevue slot props
export interface TableCellSlotProps {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	data: any;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	node: any;
	column: ColumnNode;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	field: string | ((item: any) => string) | undefined;
	index: number;
	frozenRow: boolean;
	editorInitCallback: (event: Event) => void;
	rowTogglerCallback: (event: Event) => void;
}

export enum Border {
	LEFT = 'left',
	RIGHT = 'right',
	TOP = 'top',
	BOTTOM = 'bottom',
}
